import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ReferFriend from "../components/ReferFriend";
import SocialShare from "../components/SocialShare";
import ProposalCommentThread from "../components/ProposalCommentThread";
import IndustryNotice from "../components/IndustryNotice";
import PayPalCheckout from "../components/PayPalCheckout";
import {
  CheckCircle, Shield, Loader2, AlertTriangle, ArrowRight, Clock, Code, Layers,
  Gift, BarChart3, Calendar, Lock, Sparkles, MessageSquare, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EUR_RATE = 0.92;

const DELIVERY_MULTIPLIERS = { 1: 0.60, 2: 0.75, 3: 1.00, 4: 1.35 };
// Each option is positioned as a distinct product. Discount/premium percentages
// are NOT shown to the client — only the resulting price and the included scope.
const DELIVERY_PRODUCTS = {
  1: {
    name: "Speed Build",
    label: "1 week",
    tagline: "Working AI-generated software",
    features: [
      "Working AI-generated software",
      "2 hours of iterations",
    ],
  },
  2: {
    name: "Reviewed Build",
    label: "2 weeks",
    tagline: "AI speed with human polish",
    features: [
      "Everything in Speed Build",
      "Senior developer review",
      "Bug fixing pass",
      "6 hours of iterations & consulting",
    ],
  },
  3: {
    name: "Architected Build",
    label: "3 weeks",
    tagline: "Production-ready foundations",
    features: [
      "Everything in Reviewed Build",
      "Documented database data model",
      "Data relationships diagram",
    ],
  },
  4: {
    name: "Premium MVP",
    label: "4 weeks",
    tagline: "Battle-test with a real audience",
    features: [
      "Everything in Architected Build",
      "Ready for limited production release",
      "12 hours of iterations & consulting",
      "Full documentation of every feature",
    ],
  },
};

const fmt = (amount, currency) => currency === "eur"
  ? `€${(amount * EUR_RATE).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
  : `$${amount.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const SectionCard = ({ title, icon: Icon, children, action }) => (
  <div className="bg-white border border-zinc-200 rounded-2xl p-6 mb-4">
    <div className="flex items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-2">
        <Icon size={18} className="text-zinc-700" />
        <h3 className="font-semibold text-zinc-900">{title}</h3>
      </div>
      {action}
    </div>
    {children}
  </div>
);

function TimelineViz({ phases, fallback }) {
  const safe = Array.isArray(phases) && phases.length > 0
    ? phases
    : (fallback ? [{ week: 1, phase: "Timeline", milestones: [fallback] }] : []);
  if (safe.length === 0) return null;
  const palette = [
    "from-blue-500 to-blue-600",
    "from-purple-500 to-purple-600",
    "from-emerald-500 to-emerald-600",
    "from-amber-500 to-amber-600",
    "from-rose-500 to-rose-600",
  ];
  return (
    <div data-testid="timeline-viz">
      {/* Horizontal segment bar */}
      <div className="flex rounded-full overflow-hidden h-3 mb-5 bg-zinc-100">
        {safe.map((p, i) => (
          <div key={`bar-w${p.week}`} title={`Week ${p.week} — ${p.phase}`}
            className={`flex-1 bg-gradient-to-r ${palette[i % palette.length]}`} />
        ))}
      </div>
      {/* Cards, one per phase */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {safe.map((p, i) => (
          <div key={`card-w${p.week}`} data-testid={`timeline-phase-${p.week}`}
            className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 relative">
            <div className={`absolute top-0 left-0 h-1 w-full rounded-t-xl bg-gradient-to-r ${palette[i % palette.length]}`} />
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Week {p.week}</div>
            <div className="font-bold text-zinc-950 text-sm mb-2 leading-snug">{p.phase}</div>
            <ul className="space-y-1">
              {(p.milestones || []).map((m) => (
                <li key={`w${p.week}-${m}`} className="flex items-start gap-1.5 text-[12px] text-zinc-700 leading-snug">
                  <CheckCircle size={10} className="text-emerald-600 mt-1 flex-shrink-0" /> {m}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

const LoadingState = ({ label = "AI is analyzing your project..." }) => (
  <div className="text-center py-20" data-testid="proposal-loading">
    <div className="inline-flex items-center justify-center w-16 h-16 bg-zinc-100 rounded-2xl mb-6">
      <Loader2 size={32} className="text-zinc-600 animate-spin" />
    </div>
    <h2 className="text-xl font-bold text-zinc-900 mb-2">{label}</h2>
    <p className="text-zinc-500 text-sm">This takes 15–30 seconds.</p>
  </div>
);

function DeliveryPicker({ basePrice, currency, selected, onSelect, capacityOK, disabled }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="delivery-picker">
      {[1, 2, 3, 4].map(w => {
        const isActive = selected === w;
        const price = basePrice * DELIVERY_MULTIPLIERS[w];
        const product = DELIVERY_PRODUCTS[w];
        const disabledOneWk = w === 1 && !capacityOK;
        const isPopular = w === 2;
        const isPremium = w === 4;
        const cls = disabledOneWk
          ? "bg-zinc-50 border-zinc-200 opacity-60 cursor-not-allowed"
          : isActive
            ? "bg-zinc-950 border-zinc-950 text-white shadow-lg"
            : "bg-white border-zinc-200 hover:border-zinc-400 hover:shadow-sm";
        return (
          <button key={w} disabled={disabledOneWk || disabled} onClick={() => !disabledOneWk && onSelect(w)}
            data-testid={`delivery-option-${w}`}
            className={`relative rounded-2xl p-4 text-left border-2 transition-all flex flex-col ${cls}`}>
            {isPopular && !disabledOneWk && (
              <span className={`absolute -top-2 right-2 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-full ${isActive ? "bg-white text-zinc-950" : "bg-emerald-600 text-white"}`}>
                Most popular
              </span>
            )}
            {isPremium && !disabledOneWk && (
              <span className={`absolute -top-2 right-2 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-full ${isActive ? "bg-white text-zinc-950" : "bg-zinc-900 text-white"}`}>
                Premium
              </span>
            )}
            {disabledOneWk && (
              <span className="absolute -top-2 right-2 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-full bg-amber-500 text-white inline-flex items-center gap-1">
                <Lock size={8} /> At capacity
              </span>
            )}
            <div className={`text-[10px] font-bold uppercase tracking-wider ${isActive ? "text-zinc-300" : disabledOneWk ? "text-zinc-400" : "text-zinc-500"}`}>
              {product.label}
            </div>
            <div className={`text-base font-extrabold mt-0.5 leading-tight ${isActive ? "text-white" : "text-zinc-950"}`}>
              {product.name}
            </div>
            <div className={`text-[11px] mt-0.5 leading-snug ${isActive ? "text-zinc-300" : disabledOneWk ? "text-zinc-400" : "text-zinc-500"}`}>
              {product.tagline}
            </div>
            <div className={`text-2xl font-extrabold mt-3 ${isActive ? "text-white" : disabledOneWk ? "text-zinc-400" : "text-zinc-950"}`}>
              {fmt(price, currency)}
            </div>
            <ul className="mt-3 space-y-1.5 flex-1">
              {product.features.map((f) => (
                <li key={f} className={`flex items-start gap-1.5 text-[12px] leading-snug ${isActive ? "text-zinc-200" : disabledOneWk ? "text-zinc-400" : "text-zinc-700"}`}>
                  <CheckCircle size={11} className={`mt-0.5 flex-shrink-0 ${isActive ? "text-emerald-300" : "text-emerald-600"}`} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            {disabledOneWk && (
              <div className="text-[10px] text-amber-800 font-semibold mt-2">Capacity full — try the 2-week option</div>
            )}
          </button>
        );
      })}
    </div>
  );
}

function RefineModal({ open, onClose, onSubmit, submitting }) {
  const [notes, setNotes] = useState("");
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4" onClick={() => !submitting && onClose()} data-testid="refine-modal">
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-purple-600" />
            <h3 className="font-bold text-zinc-950">Refine your scope</h3>
          </div>
          <button onClick={onClose} disabled={submitting} className="p-1 hover:bg-zinc-100 rounded-lg text-zinc-500"><X size={16} /></button>
        </div>
        <p className="text-sm text-zinc-600 mb-3">
          Describe what you'd like to add, change, or explore more. We'll regenerate the scope and price in about 15 seconds.
          Your previous quote is archived but remains on file.
        </p>
        <textarea data-testid="input-refine-notes" rows={5} value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="e.g. Add an admin dashboard with user-activity charts, and Slack notifications when a new user signs up."
          className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none" />
        <div className="flex items-center justify-between mt-2 text-[11px] text-zinc-500">
          <span>{notes.length}/2000</span>
          <span>The price may change based on what you add.</span>
        </div>
        <div className="flex gap-2 mt-4 justify-end">
          <button onClick={onClose} disabled={submitting} className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:bg-zinc-100 rounded-full">Cancel</button>
          <button data-testid="btn-submit-refine" onClick={() => onSubmit(notes)} disabled={submitting || notes.trim().length < 5}
            className="px-5 py-2 bg-purple-600 text-white text-sm font-semibold rounded-full hover:bg-purple-700 disabled:opacity-50 inline-flex items-center gap-2">
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Regenerating…</> : <><Sparkles size={14} /> Regenerate quote</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScheduleMeetingModal({ open, onClose, projectId, defaultEmail, defaultName }) {
  const [form, setForm] = useState({ name: defaultName || "", email: defaultEmail || "", phone: "", preferred_time: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.includes("@")) { setError("Name and a valid email are required"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/projects/${projectId}/contact-request`, form);
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onClose(); }, 2200);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not send request.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4" onClick={() => !submitting && onClose()} data-testid="meeting-modal">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-zinc-700" />
            <h3 className="font-bold text-zinc-950">Schedule a call to review your quote</h3>
          </div>
          <button onClick={onClose} disabled={submitting} className="p-1 hover:bg-zinc-100 rounded-lg text-zinc-500"><X size={16} /></button>
        </div>
        {success ? (
          <div className="text-center py-8" data-testid="meeting-success">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle size={22} className="text-emerald-700" />
            </div>
            <div className="font-bold text-zinc-950">Request received</div>
            <p className="text-sm text-zinc-600 mt-1">We'll respond within one business day to confirm a time.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-zinc-600">Your name *</label>
                <input required data-testid="input-meeting-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-600">Email *</label>
                <input required type="email" data-testid="input-meeting-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-zinc-600">Phone (optional)</label>
                <input data-testid="input-meeting-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                  className="mt-1 w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-600">Preferred time</label>
                <input data-testid="input-meeting-time" value={form.preferred_time} onChange={e => setForm({ ...form, preferred_time: e.target.value })}
                  placeholder="Tue/Thu after 3pm CET"
                  className="mt-1 w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-600">Anything specific you'd like to discuss?</label>
              <textarea rows={3} data-testid="input-meeting-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none" />
            </div>
            {error && <div data-testid="meeting-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            <button type="submit" disabled={submitting} data-testid="btn-submit-meeting"
              className="w-full py-3 bg-zinc-950 text-white rounded-full font-semibold text-sm hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Calendar size={14} /> Request meeting</>}
            </button>
            <p className="text-[11px] text-zinc-500 text-center">A team member will email you within one business day.</p>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Proposal() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [capacity, setCapacity] = useState({ one_week_available: true });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [agreeNda, setAgreeNda] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [selectedWeeks, setSelectedWeeks] = useState(null);
  const [selecting, setSelecting] = useState(false);

  const [refineOpen, setRefineOpen] = useState(false);
  const [refining, setRefining] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [proj, cap] = await Promise.all([
          axios.get(`${API}/projects/${projectId}`),
          axios.get(`${API}/capacity`).catch(() => ({ data: { one_week_available: true } })),
        ]);
        if (cancelled) return;
        setProject(proj.data);
        setCapacity(cap.data);
        setSelectedWeeks(proj.data?.selected_delivery_weeks || proj.data?.delivery_weeks || 3);
        let lastErr = null;
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            const prop = await axios.post(`${API}/projects/${projectId}/generate-proposal`, null, { timeout: 120000 });
            if (cancelled) return;
            setProposal(prop.data);
            lastErr = null;
            break;
          } catch (err) {
            lastErr = err;
            if (attempt === 0) await new Promise((r) => setTimeout(r, 2000));
          }
        }
        if (lastErr) throw lastErr;
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Failed to generate proposal. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectId]);

  const selectDelivery = async (weeks) => {
    if (!weeks || weeks === selectedWeeks) return;
    const prev = selectedWeeks;
    setSelectedWeeks(weeks);
    setSelecting(true);
    try {
      await axios.patch(`${API}/projects/${projectId}/select-delivery`, { delivery_weeks: weeks });
    } catch (e) {
      setSelectedWeeks(prev);
      alert(e.response?.data?.detail || "Could not select this option");
    } finally { setSelecting(false); }
  };

  const handlePayment = async () => {
    setPayLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        project_id: projectId, origin_url: window.location.origin,
        currency: project?.currency || "usd", payment_type: "upfront_50",
      });
      window.location.href = res.data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create payment. Please try again.");
      setPayLoading(false);
    }
  };

  const submitRefine = async (notes) => {
    setRefining(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/refine`, { refinement_notes: notes });
      setProposal(res.data);
      setRefineOpen(false);
    } catch (e) {
      alert(e.response?.data?.detail || "Could not refine. Please try again.");
    } finally { setRefining(false); }
  };

  const currency = project?.currency || "usd";
  const basePrice = proposal?.final_price_usd || 6000;
  const mult = DELIVERY_MULTIPLIERS[selectedWeeks] || 1.0;
  const price = project?.price_override || (basePrice * mult);
  const upfront = price * 0.5;
  const canPay = agreeNda && agreeTerms && selectedWeeks && (!(selectedWeeks === 1 && !capacity.one_week_available));

  if (loading) return <div className="min-h-screen bg-white"><Navbar /><div className="max-w-3xl mx-auto px-4 pt-24"><LoadingState /></div><Footer /></div>;
  if (error) return (
    <div className="min-h-screen bg-white"><Navbar />
      <div className="max-w-xl mx-auto px-4 pt-24 text-center" data-testid="proposal-error">
        <AlertTriangle size={40} className="text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-zinc-900 mb-2">Generation Failed</h2>
        <p className="text-zinc-600 mb-6">{error}</p>
        <button onClick={() => navigate("/build")} className="px-6 py-3 bg-zinc-950 text-white rounded-full text-sm font-semibold hover:bg-zinc-800 transition-colors">Try Again</button>
      </div>
      <Footer />
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="proposal-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-24">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-semibold mb-4">
            <CheckCircle size={13} /> Scope Generated by AI
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-950 mb-2">{proposal?.project_summary?.split(".")[0]}</h1>
          <p className="text-zinc-500 text-sm">For {project?.client_name} · {project?.industry}</p>
        </div>

        {proposal?.is_flagged && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-600 mt-0.5" />
            <div><div className="font-semibold text-amber-800 text-sm">Complex Scope Detected</div>
            <div className="text-amber-700 text-xs mt-1">This project exceeds typical MVP scope. Consider reducing features for first version or refining the scope below.</div></div>
          </div>
        )}

        <SectionCard title="Project Summary" icon={Layers}>
          <p className="text-zinc-700 text-sm leading-relaxed">{proposal?.project_summary}</p>
        </SectionCard>

        <SectionCard title="Identified Components" icon={Code}>
          <div className="space-y-2">
            {proposal?.components_identified?.map((c, i) => (
              <div key={i} className="flex items-start gap-3 py-2 border-b border-zinc-50 last:border-0">
                <CheckCircle size={14} className="text-green-600 mt-1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-800">{c.name}</div>
                  <div className="text-xs text-zinc-500">{c.description}</div>
                  <ProposalCommentThread projectId={projectId} kind="component" index={i} email={project?.client_email} />
                </div>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Key Features" icon={CheckCircle}
          action={
            <button data-testid="btn-open-refine" onClick={() => setRefineOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-purple-700 hover:text-purple-800 bg-purple-50 hover:bg-purple-100 px-3 py-1.5 rounded-full border border-purple-200">
              <Sparkles size={12} /> Refine / add features
            </button>
          }>
          <ul className="space-y-2">
            {proposal?.features_list?.map((f, i) => (
              <li key={i} className="text-sm text-zinc-700">
                <div className="flex items-start gap-2">
                  <CheckCircle size={13} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="flex-1">{f}</span>
                </div>
                <div className="ml-5">
                  <ProposalCommentThread projectId={projectId} kind="feature" index={i} email={project?.client_email} />
                </div>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-zinc-500 mt-3 border-t border-zinc-100 pt-3">
            Click <em>Comment</em> on any item to discuss specifics with the team without changing scope. Need to add or remove features? Use <em>Refine / add features</em> — the AI will regenerate the quote.
          </p>
        </SectionCard>

        {project?.industry && (
          <div className="mb-4" data-testid="proposal-industry-notice">
            <IndustryNotice industry={project.industry} />
          </div>
        )}

        <SectionCard title="Tech Stack" icon={Code}>
          <div className="flex flex-wrap gap-2">{proposal?.tech_stack?.map((t, i) => (
            <span key={i} className="px-2.5 py-1 bg-zinc-100 text-zinc-700 text-xs font-medium rounded-full border border-zinc-200">{t}</span>
          ))}</div>
        </SectionCard>

        <SectionCard title="Timeline" icon={Clock}>
          <TimelineViz phases={proposal?.timeline_phases} fallback={proposal?.timeline_breakdown} />
          {proposal?.timeline_breakdown && (
            <p className="text-xs text-zinc-500 mt-4 border-t border-zinc-100 pt-3 whitespace-pre-line">{proposal.timeline_breakdown}</p>
          )}
        </SectionCard>

        <div className="bg-white border border-zinc-200 rounded-2xl p-6 mb-4" data-testid="quote-card">
          <div className="flex items-baseline justify-between mb-1">
            <h3 className="font-semibold text-zinc-900">Choose your build package</h3>
            <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">Locked quote</span>
          </div>
          <p className="text-xs text-zinc-600 mb-4">Each package is a complete product with a different level of human review, documentation, and consulting included.</p>

          {!capacity.one_week_available && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-start gap-2 text-xs text-amber-900" data-testid="capacity-notice">
              <Lock size={13} className="mt-0.5" />
              <span>{capacity.reason || "1-week delivery is temporarily unavailable due to capacity constraints."}</span>
            </div>
          )}

          <DeliveryPicker basePrice={basePrice} currency={currency} selected={selectedWeeks} onSelect={selectDelivery}
            capacityOK={capacity.one_week_available} disabled={selecting} />

          <div className="mt-6 flex items-baseline justify-between py-3 border-t border-zinc-100">
            <span className="text-zinc-700 text-sm">Fixed project price</span>
            <span className="text-zinc-950 text-2xl font-extrabold" data-testid="total-price">{fmt(price, currency)}</span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-4">
            <div className="bg-zinc-50 rounded-xl p-4 text-center">
              <div className="text-xs text-zinc-500 mb-1">Due Now (50%)</div>
              <div className="text-xl font-extrabold text-zinc-950" data-testid="upfront-price">{fmt(upfront, currency)}</div>
            </div>
            <div className="bg-zinc-50 rounded-xl p-4 text-center">
              <div className="text-xs text-zinc-500 mb-1">Due on Delivery (50%)</div>
              <div className="text-xl font-extrabold text-zinc-950">{fmt(upfront, currency)}</div>
            </div>
          </div>
          <div className="mt-4 text-[11px] text-zinc-500 leading-relaxed border-t border-zinc-100 pt-3" data-testid="quote-disclaimer">
            This quote reflects the scope described on this page. Refining features or adding scope may update the price — you'll see the new quote instantly.
          </div>
        </div>

        {proposal?.bonus_addon && (
          <div data-testid="bonus-addon-card"
            className="relative overflow-hidden rounded-2xl p-6 mb-4 border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
            <div className="absolute -top-16 -right-16 w-48 h-48 bg-emerald-200/40 rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center flex-shrink-0">
                    <Gift size={18} className="text-white" />
                  </div>
                  <div>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider rounded-full mb-1">
                      <Gift size={10} /> Bonus Included
                    </div>
                    <h3 className="font-bold text-zinc-950 text-lg leading-tight flex items-center gap-2">
                      <BarChart3 size={18} className="text-emerald-700" />
                      {proposal.bonus_addon.name}
                    </h3>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-zinc-500 line-through">${(proposal.bonus_addon.value_usd || 0).toLocaleString()}</div>
                  <div className="text-xl font-extrabold text-emerald-700">FREE</div>
                </div>
              </div>
              <p className="text-zinc-700 text-sm mt-3 leading-relaxed">{proposal.bonus_addon.tagline}</p>
              <ul className="mt-4 space-y-2">
                {(proposal.bonus_addon.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-zinc-700">
                    <CheckCircle size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {proposal.bonus_addon.promo_label && (
                <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-800 bg-emerald-100 px-3 py-1.5 rounded-full">
                  <Clock size={12} /> {proposal.bonus_addon.promo_label}
                </div>
              )}
              {proposal.bonus_addon.fine_print && (
                <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">{proposal.bonus_addon.fine_print}</p>
              )}
            </div>
          </div>
        )}

        <div className="bg-white border border-zinc-200 rounded-2xl p-6 mb-4" data-testid="meeting-cta-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
              <MessageSquare size={18} className="text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="font-bold text-zinc-950 text-sm">Questions about the quote?</div>
              <p className="text-xs text-zinc-600 mt-0.5">Book a short call with the team before paying — we'll walk through scope, timeline, and any custom requirements.</p>
              <button data-testid="btn-open-meeting" onClick={() => setMeetingOpen(true)}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 bg-white border border-zinc-200 rounded-full hover:bg-zinc-50 text-zinc-900">
                <Calendar size={12} /> Schedule a call to review
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-2xl p-6 mb-6">
          <h3 className="font-semibold text-zinc-900 mb-4">Before You Pay</h3>
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid="checkbox-agree-nda" checked={agreeNda} onChange={e => setAgreeNda(e.target.checked)} className="mt-0.5" />
              <span className="text-sm text-zinc-600">I have read and agree to the <a href="/legal#nda" target="_blank" className="underline text-zinc-900 hover:text-blue-600">Non-Disclosure Agreement</a>. All project details will be kept strictly confidential.</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid="checkbox-agree-terms" checked={agreeTerms} onChange={e => setAgreeTerms(e.target.checked)} className="mt-0.5" />
              <span className="text-sm text-zinc-600">I agree to the <a href="/legal#terms" target="_blank" className="underline text-zinc-900 hover:text-blue-600">Terms of Service</a> and confirm I understand that <strong>I retain full intellectual property ownership</strong> upon final payment.</span>
            </label>
          </div>
          <button data-testid="btn-proceed-payment" onClick={handlePayment} disabled={!canPay || payLoading}
            className="w-full mt-6 py-4 bg-zinc-950 text-white rounded-full font-semibold text-sm hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2">
            {payLoading ? <><Loader2 size={16} className="animate-spin" /> Redirecting to payment...</>
              : <>Proceed to Payment — {fmt(upfront, currency)} (50%) <ArrowRight size={16} /></>}
          </button>
          <div className="flex items-center justify-center gap-4 mt-4 text-xs text-zinc-400">
            <span className="flex items-center gap-1"><Shield size={11} />Secured by Stripe</span>
            <span className="flex items-center gap-1"><Shield size={11} />GDPR Compliant</span>
            <span className="flex items-center gap-1"><CheckCircle size={11} />NDA Protected</span>
          </div>
          <PayPalCheckout
            projectId={projectId}
            currency={currency}
            paymentType="upfront_50"
            disabled={!canPay}
            onError={(msg) => setError(msg)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
          <ReferFriend shareUrl={`${window.location.origin}`} />
          <SocialShare />
        </div>
      </div>

      <RefineModal open={refineOpen} onClose={() => setRefineOpen(false)} onSubmit={submitRefine} submitting={refining} />
      {refining && (
        <div className="fixed inset-0 bg-white/90 z-40 flex items-center justify-center" data-testid="refining-overlay">
          <LoadingState label="Regenerating your quote with your refinements…" />
        </div>
      )}
      <ScheduleMeetingModal open={meetingOpen} onClose={() => setMeetingOpen(false)}
        projectId={projectId} defaultEmail={project?.client_email} defaultName={project?.client_name} />

      <Footer />
    </div>
  );
}
